'use client';
import type { MuxCSSProperties } from '@mux/mux-player-react';
import cn from '@utils/cn';
import { isWindowDefined } from '@utils/validations';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useMemo } from 'react';

type FilePlayerProps = {
  playbackId?: string;
  filename?: string;
  aspect_ratio?: string;
  thumbTime?: number;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
  opacity?: number;
  className?: string;
  isEagerLoad?: boolean;
};
export default function VideoPlayer({
  playbackId,
  filename,
  aspect_ratio,
  thumbTime,
  autoPlay,
  muted,
  loop,
  controls,
  opacity,
  className,
  isEagerLoad,
}: FilePlayerProps) {
  const posterUrl = playbackId
    ? thumbTime !== undefined && thumbTime !== null
      ? `https://image.mux.com/${playbackId}/thumbnail.webp?time=${thumbTime}`
      : `https://image.mux.com/${playbackId}/thumbnail.webp`
    : undefined;

  const aspectRatio = aspect_ratio ?? '16/9';

  const containerStyles: React.CSSProperties = {
    aspectRatio: aspectRatio.replace(':', '/'),
  };

  const styles: MuxCSSProperties = {
    width: '100%',
    height: '100%',
    display: 'block',
    opacity: opacity ?? 1,
    aspectRatio: aspectRatio.replace(':', '/'),
    '--media-object-fit': 'contain',
    '--media-object-position': 'center',
    '--media-background-color': 'transparent',
  };
  if (!controls) {
    styles['--controls'] = 'none';
  }

  const viewerId = isWindowDefined ? window.anonymousId : undefined;

  const MuxPlayerNoSSR = useMemo(() => {
    return dynamic(() => import('@mux/mux-player-react').then((m) => m.default), {
      ssr: false,
      loading: () =>
        posterUrl ? (
          <Image
            src={posterUrl}
            alt={filename ?? 'Video Poster'}
            fill
            style={{
              objectFit: 'contain',
              objectPosition: 'center',
              opacity: opacity ?? 1,
            }}
            priority={isEagerLoad ?? false}
            fetchPriority={isEagerLoad ? 'high' : 'low'}
            className={className}
          />
        ) : null,
    });
  }, [filename, isEagerLoad, posterUrl, className, opacity]);

  if (!playbackId) {
    return null;
  }

  return (
    <div style={containerStyles} className={cn('relative flex h-auto w-full overflow-hidden', className)}>
      <MuxPlayerNoSSR
        playbackId={playbackId}
        autoPlay={autoPlay ?? true}
        muted={muted ?? true}
        loop={loop ?? true}
        {...(filename && {
          title: filename,
          metadataVideoTitle: filename,
        })}
        poster={posterUrl}
        placeholder={posterUrl}
        {...(viewerId && { metadataViewerUserId: viewerId })}
        streamType="on-demand"
        style={styles}
        preload="auto"
        disableTracking
        className={className}
      />
    </div>
  );
}
